import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { resolveUrl } from "@/components/ImageUploader";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CheckoutPageWrapper() {
  const { slug } = useParams();
  return (
    <CartProvider slug={slug}>
      <CheckoutPage />
    </CartProvider>
  );
}

function CheckoutPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { items, clear } = useCart();
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [form, setForm] = useState({
    name: user?.name || "", surname: user?.surname || "", email: user?.email || "", phone: user?.phone || "",
    address: user?.address || "", city: user?.city || "", cap: user?.cap || "", country: "Italia",
    consenso_riv: false, consenso_cm: false,
  });
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;
    axios.post(`${API}/public/cart/price`, { tenant_slug: slug, items: items.map(({ sku_variante, qta }) => ({ sku_variante, qta })) })
      .then((r) => setPricing(r.data));
  }, [items, slug]);

  if (items.length === 0 && !confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-center px-6" data-testid="checkout-empty">
        <div>
          <h1 className="font-heading font-bold text-3xl mb-2">Il tuo carrello è vuoto</h1>
          <Link to={`/s/${slug}`} className="text-brand hover:underline text-sm">Torna alla vetrina</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso_riv) { toast.error("Consenso obbligatorio"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/cart/checkout`, {
        tenant_slug: slug,
        items: items.map(({ sku_variante, qta }) => ({ sku_variante, qta })),
        customer: { name: `${form.name} ${form.surname}`.trim(), surname: form.surname, email: form.email, phone: form.phone },
        shipping_address: { via: form.address, citta: form.city, cap: form.cap, paese: form.country },
        payment_method: paymentMethod,
        consenso_marketing_rivenditore: form.consenso_riv,
        consenso_marketing_casa_madre: form.consenso_cm,
      });
      setConfirmed(res.data);
      clear();
      toast.success(`Ordine ${res.data.numero_ordine} confermato!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSubmitting(false); }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="checkout-confirmed">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-700" />
          </div>
          <h1 className="editorial-h1 text-4xl mb-3">Grazie {form.name}!</h1>
          <p className="text-muted-foreground mb-2">Ordine <b className="font-mono text-foreground">{confirmed.numero_ordine}</b> confermato.</p>
          <p className="text-sm text-muted-foreground mb-6">Riceverai una mail di conferma a <b>{form.email}</b> con il riepilogo e i dettagli di tracking.</p>
          <p className="text-sm text-muted-foreground mb-6">Totale pagato: <b className="text-foreground">€ {confirmed.totale}</b> · Pagamento {paymentMethod.toUpperCase()} (MOCK)</p>
          <div className="flex gap-2 justify-center">
            <Link to={`/s/${slug}`} className="px-5 h-11 inline-flex items-center rounded-sm border border-border text-sm hover:bg-accent">Torna al catalogo</Link>
            {user?.role === "customer" && (
              <Link to="/account" className="px-5 h-11 inline-flex items-center rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover">Vedi i miei ordini</Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to={`/s/${slug}`} data-testid="checkout-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Continua gli acquisti
          </Link>
          <div className="font-heading font-bold">{pricing?.tenant_name}</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 space-y-6" data-testid="checkout-form">
          <div>
            <h2 className="font-heading font-bold text-2xl mb-4">Dati di spedizione</h2>
            <div className="grid grid-cols-2 gap-3">
              <Input k="name" label="Nome*" form={form} setForm={setForm} required />
              <Input k="surname" label="Cognome*" form={form} setForm={setForm} required />
              <Input k="email" type="email" label="Email*" form={form} setForm={setForm} required colSpan={2} />
              <Input k="phone" label="Telefono*" form={form} setForm={setForm} required />
              <Input k="country" label="Paese" form={form} setForm={setForm} />
              <Input k="address" label="Indirizzo*" form={form} setForm={setForm} required colSpan={2} />
              <Input k="city" label="Città*" form={form} setForm={setForm} required />
              <Input k="cap" label="CAP*" form={form} setForm={setForm} required />
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl mb-4">Metodo di pagamento</h2>
            <div className="grid grid-cols-3 gap-2">
              {["stripe", "paypal", "klarna"].map((m) => (
                <button key={m} type="button" data-testid={`pay-${m}`} onClick={() => setPaymentMethod(m)}
                  className={`px-3 h-11 rounded-sm border text-sm font-medium ${paymentMethod === m ? "border-brand bg-brand/10 text-brand" : "border-border hover:bg-accent"}`}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Provider in modalità DEMO/MOCK per questa release.</p>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <label className="flex items-start gap-2 cursor-pointer text-xs leading-relaxed">
              <Checkbox data-testid="ck-consent-riv" checked={form.consenso_riv} onCheckedChange={(v) => setForm({ ...form, consenso_riv: !!v })} required />
              <span>Acconsento al trattamento dei miei dati da parte di <b>{pricing?.tenant_name}</b> per gestione ordine. <span className="text-brand">(Obbligatorio)</span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-xs leading-relaxed">
              <Checkbox data-testid="ck-consent-cm" checked={form.consenso_cm} onCheckedChange={(v) => setForm({ ...form, consenso_cm: !!v })} />
              <span>Acconsento anche a ricevere offerte dal Brand della rete. <span className="text-muted-foreground">(Facoltativo)</span></span>
            </label>
          </div>

          <button type="submit" disabled={submitting} data-testid="checkout-submit"
            className="w-full h-12 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Conferma ordine · € {pricing?.total || "—"}
          </button>
        </form>

        <aside className="bg-[#FAFAFA] border border-border rounded-sm p-5 h-fit">
          <h3 className="font-heading font-bold text-lg mb-4">Riepilogo</h3>
          {pricing?.items?.map((it) => (
            <div key={it.sku_variante} className="flex gap-3 mb-3" data-testid={`summary-${it.sku_variante}`}>
              <div className="w-16 h-16 bg-white overflow-hidden flex-shrink-0">
                {it.immagine && <img src={resolveUrl(it.immagine)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 text-xs">
                <div className="font-medium">{it.nome}</div>
                <div className="text-muted-foreground">Qta: {it.qta}</div>
              </div>
              <div className="text-sm font-medium">€ {it.totale_riga}</div>
            </div>
          ))}
          {pricing && (
            <div className="border-t border-border pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotale</span><span>€ {pricing.subtotal}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Spedizione</span><span>{pricing.shipping === 0 ? "Gratis" : `€ ${pricing.shipping}`}</span></div>
              <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t border-border"><span>Totale</span><span>€ {pricing.total}</span></div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

const Input = ({ k, label, form, setForm, type = "text", required, colSpan = 1 }) => (
  <div className={colSpan === 2 ? "col-span-2" : ""}>
    <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
    <input data-testid={`ck-${k}`} type={type} value={form[k] || ""} required={required}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
  </div>
);
